import { useEffect, useRef, useState } from 'react';
import { API_URL } from '../env';

const StreamingOutput = ({ endpoint, enabled, onComplete, onError }) => {
  const [content, setContent] = useState('');
  const [status, setStatus] = useState('idle');
  const [coverage, setCoverage] = useState(null);
  const [warning, setWarning] = useState('');
  const [chunkCount, setChunkCount] = useState(0);
  const [startedAt, setStartedAt] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const sourceRef = useRef(null);
  const contentRef = useRef('');
  const contentViewRef = useRef(null);
  const followTailRef = useRef(true);

  const isNearTail = (el) => (el.scrollHeight - el.scrollTop - el.clientHeight) <= 24;

  const handleContentScroll = () => {
    const el = contentViewRef.current;
    if (!el) return;
    followTailRef.current = isNearTail(el);
  };

  useEffect(() => {
    if (!enabled || !endpoint) {
      return undefined;
    }

    setContent('');
    contentRef.current = '';
    setStatus('connecting');
    setCoverage(null);
    setWarning('');
    setChunkCount(0);
    setStartedAt(Date.now());
    setElapsedSeconds(0);
    followTailRef.current = true;

    const source = new EventSource(`${API_URL}${endpoint}`, { withCredentials: true });
    sourceRef.current = source;

    source.addEventListener('open', () => {
      setStatus('streaming');
    });

    source.addEventListener('chunk', (event) => {
      try {
        const payload = JSON.parse(event.data || '{}');
        if (payload.chunk) {
          setChunkCount((prev) => prev + 1);
          setContent((prev) => {
            const next = prev + payload.chunk;
            contentRef.current = next;
            return next;
          });
        }
      } catch {
        // Ignore malformed chunk payloads.
      }
    });

    source.addEventListener('complete', (event) => {
      setStatus('complete');
      source.close();
      sourceRef.current = null;
      try {
        const payload = JSON.parse(event.data || '{}');
        onComplete?.(payload);
      } catch {
        onComplete?.({ content: contentRef.current });
      }
    });

    source.addEventListener('coverage', (event) => {
      try {
        setCoverage(JSON.parse(event.data || '{}'));
      } catch {
        setCoverage(null);
      }
    });

    source.addEventListener('warning', (event) => {
      try {
        const payload = JSON.parse(event.data || '{}');
        setWarning(payload.message || 'Merge warning');
      } catch {
        setWarning('Merge warning');
      }
    });

    source.addEventListener('error', (event) => {
      setStatus('error');
      source.close();
      sourceRef.current = null;
      onError?.(event);
    });

    return () => {
      source.close();
      sourceRef.current = null;
    };
  }, [enabled, endpoint, onComplete, onError]);

  useEffect(() => {
    if (status !== 'streaming' || !startedAt) {
      return undefined;
    }
    const timer = setInterval(() => {
      const nextElapsed = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
      setElapsedSeconds(nextElapsed);
    }, 1000);
    return () => clearInterval(timer);
  }, [status, startedAt]);

  useEffect(() => {
    const el = contentViewRef.current;
    if (!el || !followTailRef.current) {
      return;
    }
    el.scrollTop = el.scrollHeight;
  }, [content]);

  const shouldRender = enabled || status !== 'idle' || content.length > 0;
  if (!shouldRender) {
    return null;
  }

  return (
    <section className="panel panel--spaced streaming-output" aria-live="polite">
      <div className="streaming-output__header">
        <h3>Streaming Output</h3>
        <div className="streaming-output__meta">
          <span className={`status-pill status-pill--${status}`}>{status}</span>
          {(status === 'streaming' || status === 'complete') && (
            <span className="streaming-output__stats">
              {chunkCount} chunks{elapsedSeconds > 0 ? ` • ${elapsedSeconds}s` : ''}
            </span>
          )}
        </div>
      </div>
      <pre
        className="streaming-output__content"
        ref={contentViewRef}
        onScroll={handleContentScroll}
      >
        {content || 'Waiting for model output...'}
      </pre>
      {coverage && (
        <p className="muted">
          Coverage score: {Math.round((coverage.overall_coverage_score || 0) * 100)}% | Merged words:{' '}
          {coverage.merged_word_count || 0}
        </p>
      )}
      {warning && <p className="alert">{warning}</p>}
    </section>
  );
};

export default StreamingOutput;
